/**
 * A tool's board — the constellation, or the genogram and ecomap — opened
 * inside the call rather than in a tab of its own.
 *
 * Each side frames its own page: the therapist their board, the patient the
 * page their share link opens. What travels on the data channel is only the
 * patient's half — the path their link opens — because the therapist's page is
 * one only the therapist can load, and a patient told where it was would only
 * be shown a sign-in screen.
 *
 * LiveKit multiplexes one data channel, so the board's traffic carries its own
 * topic and never has to be told apart from the whiteboard's or the gong's.
 */
export const TOOL_BOARD_TOPIC = "alchely-tool-board";

export const TOOL_BOARD_KINDS = ["constellation", "systemic-map"] as const;
export type ToolBoardKind = (typeof TOOL_BOARD_KINDS)[number];

/** What the patient's screen frames while a board is up. */
export type PatientToolBoard = {
    kind: ToolBoardKind;
    /** Same-origin path of the patient's page, token included. */
    patientPath: string;
};

/** What the therapist's screen frames: their own page, and the patient's half to send. */
export type TherapistToolBoard = PatientToolBoard & {
    /** Same-origin path of the therapist's chrome-less board. */
    therapistPath: string;
};

export type ToolBoardMessage =
    /** A board is up: the whole of it, so a dropped packet self-heals. */
    | { kind: "open"; board: PatientToolBoard }
    /** The board was put away. Back to the faces. */
    | { kind: "close" }
    /** Someone joined late and needs to know whether a board is up. */
    | { kind: "sync-request" };

/**
 * The window message a framed board posts to the call around it when the
 * therapist ends the board's own session from inside it.
 */
export const TOOL_BOARD_CLOSED_MESSAGE = "alchely:meeting-board-closed";

/**
 * The window message a framed board posts when its fullscreen control is
 * used (`on`: put it up, or take it down). The call answers by putting the
 * board up together with the faces, rather than the frame alone.
 */
export const TOOL_BOARD_FULLSCREEN_MESSAGE = "alchely:meeting-board-fullscreen";

/** What the call posts back into the frame: whether the board is fullscreen. */
export const TOOL_BOARD_FULLSCREEN_STATE_MESSAGE = "alchely:meeting-board-fullscreen-state";

/** The most faces a fullscreen board keeps beside it before showing only the speaker's. */
export const MAX_FULLSCREEN_FACES = 4;

/**
 * The faces kept beside a board in fullscreen.
 *
 * Everybody, up to MAX_FULLSCREEN_FACES. Past that a column of thumbnails
 * would eat the board, so only the person being listened to stays — or,
 * before anybody has spoken, the first face that is not your own.
 */
export function fullscreenFaces<T>(
    faces: T[],
    speaker: string | null,
    identityOf: (face: T) => string,
    isLocal: (face: T) => boolean,
): T[] {
    if (faces.length <= MAX_FULLSCREEN_FACES) return faces;
    const shown =
        faces.find((face) => speaker !== null && identityOf(face) === speaker) ??
        faces.find((face) => !isLocal(face)) ??
        faces[0];
    return shown ? [shown] : [];
}

/**
 * Whether a participant may put a board up for the room.
 *
 * The same rule as the meditation and bilateral stimulation: it reads the
 * identity the meeting service minted (`therapist-*` for an account holder,
 * `guest-*` for someone on a share link), and both sides apply it.
 */
export function mayControlToolBoard(identity: string): boolean {
    return identity.startsWith("therapist-");
}

/**
 * The only pages a patient's frame may be pointed at, by tool.
 *
 * A packet is whatever the other browser chose to send. A path that is not
 * one of these — another route of the app, or anything with a scheme or a
 * host — is refused rather than framed, so a forged packet cannot put an
 * arbitrary page in front of the patient.
 */
const PATIENT_PATHS: Record<ToolBoardKind, RegExp> = {
    constellation: /^\/constellation\/patient\/[A-Za-z0-9_-]+$/,
    "systemic-map": /^\/systemic-map\/patient\/[A-Za-z0-9_-]+$/,
};

export function isPatientBoardPath(kind: ToolBoardKind, path: string): boolean {
    return PATIENT_PATHS[kind].test(path);
}

/** The longest name either board keeps for a participant. */
const MAX_BOARD_NAME_LENGTH = 60;

/**
 * The page the patient's frame opens: their link, with the name they joined
 * the call under, so the board does not ask a second time who they are.
 *
 * Added on the patient's own side, from their own call, rather than sent with
 * the board — the therapist's packet carries the link alone, and is refused if
 * it carries anything more.
 */
export function patientFrameSrc(patientPath: string, name: string): string {
    const trimmed = name.trim().slice(0, MAX_BOARD_NAME_LENGTH).trim();
    if (!trimmed) return patientPath;
    return `${patientPath}?${new URLSearchParams({ name: trimmed }).toString()}`;
}

const decoder = new TextDecoder();

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function isKind(value: unknown): value is ToolBoardKind {
    return typeof value === "string" && (TOOL_BOARD_KINDS as readonly string[]).includes(value);
}

/** Read a packet from the room, or `null` if it is not one we understand. */
export function parseToolBoardMessage(payload: Uint8Array): ToolBoardMessage | null {
    let raw: unknown;
    try {
        raw = JSON.parse(decoder.decode(payload));
    } catch {
        return null;
    }
    if (!isRecord(raw)) return null;
    switch (raw.kind) {
        case "open": {
            const board = raw.board;
            if (!isRecord(board)) return null;
            const { kind, patientPath } = board;
            if (!isKind(kind) || typeof patientPath !== "string") return null;
            if (!isPatientBoardPath(kind, patientPath)) return null;
            return { kind: "open", board: { kind, patientPath } };
        }
        case "close":
        case "sync-request":
            return { kind: raw.kind };
        default:
            return null;
    }
}
